import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useUser } from './UserContext';
import { useUserOrganizations } from '../assistants/drew/hooks/useOrganization';
import { subscribeToOrganizationMembers, ROLE_DEFINITIONS } from '../services/roleService';
import { canViewAsRole, visibleMembersFor, lensRolesFor, memberUnits } from '../services/viewAs';
import { setActingContext, viewAsIsReadOnly } from '../services/actingContext';

/**
 * DemoRoleContext — "View as": preview the app through someone else's eyes.
 *
 * Two modes:
 *  1. MEMBER MODE — org admins pick a REAL member of the active org; queryScope
 *     derives from that member's actual role entry (Hugo_ToolAccess) so every
 *     page shows exactly what that person sees. personaUser becomes the member.
 *  2. PERSONA MODE (fallback) — generic role lenses resolved against the active
 *     org's first site/department/team, as before.
 *
 * Both persist in localStorage so refreshes keep the view.
 */

const STORAGE_KEY = 'hugo-demo-persona';
const DEFAULT_PERSONA_ID = 'admin';
const viewAsStorageKey = (uid, orgId) => `repsit_viewas_${uid}_${orgId}`;

/** Real member role → closest generic persona id (drives DemoAccessGate gating). */
const ROLE_TO_PERSONA = {
  org_admin: 'admin',
  org_it: 'org_it',
  executive: 'executive',
  site_manager: 'site_manager',
  department_lead: 'dh',
  team_lead: 'fls',
  respondent: 'itm',
};
export const PERSONA_TO_ROLE_KEY = Object.fromEntries(Object.entries(ROLE_TO_PERSONA).map(([r, p]) => [p, r]));

export const DEMO_PERSONAS = [
  { id: 'admin', label: 'Administrator', description: 'Organization administrator — every site, department and team' },
  { id: 'org_it', label: 'Org IT', description: 'Interfaces with Org Software/Databases' },
  { id: 'executive', label: 'Executive', description: 'Enterprise-level dashboard across all sites' },
  { id: 'site_manager', label: 'Facility/Site/Plant Manager', description: 'Site-scoped dashboards and surveys' },
  { id: 'dh', label: 'Department Head', description: 'Department-scoped scores and team rollups' },
  { id: 'fls', label: 'Frontline Supervisor / Team Leader', description: 'NWT-scoped view with the L1 review queue' },
  { id: 'itm', label: 'Individual Contributor', description: 'Own OE submissions plus their NWT snapshot' },
  { id: 'ppi', label: 'REPSit.ai Tech Support', description: 'REPSit.ai internal — tech fixes/adjustments' },
];

const DemoRoleContext = createContext(null);

/**
 * Resolve where the persona sits in the ACTIVE org's hierarchy.
 * Returns { userId, nwtId, deptId, siteId, organizationId } — each level filled
 * to the most specific real unit available, or null when the org has none.
 */
function resolveOrgScope(personaId, userId, org) {
  const base = { userId, nwtId: null, deptId: null, siteId: null, organizationId: org?.id || null };
  if (!org) return base;

  const sites = org.sites || [];
  const departments = org.departments || [];
  const teams = org.teams || [];

  // Admin / PPI / Org IT / Executive: org-level visibility (admin & ppi are
  // treated as unconstrained by trlService — personaId drives that).
  if (personaId === 'admin' || personaId === 'ppi' || personaId === 'org_it' || personaId === 'executive') {
    return base;
  }

  // Site manager: first site of the org
  if (personaId === 'site_manager') {
    return { ...base, siteId: sites[0]?.id || null };
  }

  // DH: first department that has teams (fallback: first department)
  if (personaId === 'dh') {
    const dept = departments.find((d) => teams.some((t) => t.departmentId === d.id)) || departments[0] || null;
    const site = dept ? sites.find((s) => s.id === dept.siteId) : null;
    return { ...base, deptId: dept?.id || null, siteId: site?.id || null };
  }

  // FLS / ITM: first team of the DH department (fallback: first team in org)
  if (personaId === 'fls' || personaId === 'itm') {
    const dept = departments.find((d) => teams.some((t) => t.departmentId === d.id)) || departments[0] || null;
    const team = (dept && teams.find((t) => t.departmentId === dept.id)) || teams[0] || null;
    const teamDept = team ? departments.find((d) => d.id === team.departmentId) : null;
    const site = teamDept ? sites.find((s) => s.id === teamDept.siteId) : null;
    return {
      ...base,
      nwtId: team?.id || null,
      deptId: teamDept?.id || null,
      siteId: site?.id || null,
    };
  }

  return base;
}

/**
 * Resolve where a REAL MEMBER sits in the org hierarchy from their role entry
 * ({ role, scopeId, scopeType }). Walks up dept/site from the org structure.
 */
function resolveMemberScope(member, org) {
  const base = { userId: member.uid, nwtId: null, deptId: null, siteId: null, organizationId: org?.id || null };
  if (!org) return base;
  // Org-wide roles are unconstrained; everyone else is fenced to their unit (P4-09 stores
  // nwtId/deptId/siteId on the member; older entries resolve from scopeId — viewAs.memberUnits).
  if (['org_admin', 'org_it', 'executive'].includes(member.role)) return base;
  const units = memberUnits(member, org);
  if (member.role === 'site_manager') return { ...base, siteId: units.siteId };
  if (member.role === 'department_lead') return { ...base, deptId: units.deptId, siteId: units.siteId };
  return { ...base, nwtId: units.nwtId, deptId: units.deptId, siteId: units.siteId };
}

const SUPERADMIN_EMAILS = ['alex.samaniego@ppiweb.com', 'tim.autrey@ppiweb.com'];

export function DemoRoleProvider({ children }) {
  const { user } = useAuth();
  const { roles } = useUser();
  const { organizations } = useUserOrganizations(user?.uid);
  const activeOrg = organizations[0] || null;

  const [basePersonaId, setPersonaIdState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PERSONA_ID;
    return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_PERSONA_ID;
  });

  // ── Role enforcement for real members ──────────────────────────────────────
  // Scoped members (site_manager / department_lead / team_lead / respondent)
  // are LOCKED to the persona matching their actual Hugo_ToolAccess role: the
  // access matrix, sidebar and query scoping all key off personaId, so without
  // this lock every member landed on the Administrator lens and saw everything.
  const ownRoleEntry = (activeOrg?.id && roles && roles[activeOrg.id]) || null;
  const isSuperadmin = SUPERADMIN_EMAILS.includes((user?.email || '').toLowerCase());
  const isPrivileged =
    isSuperadmin ||
    ownRoleEntry?.role === 'org_admin' ||
    Boolean(user?.uid && Array.isArray(activeOrg?.admins) && activeOrg.admins.includes(user.uid));
  const lockedPersonaId = !isPrivileged && activeOrg
    ? ROLE_TO_PERSONA[ownRoleEntry?.role] || 'itm'
    : null;

  // ── View-as: real-member impersonation lens ────────────────────────────────
  // Only the real signed-in user being an org admin (uid in org.admins) grants it.
  // P4-04 / P4-08: View As is open to everyone above individual contributor (downward only) and to
  // superadmins even when they are not in `admins[]`. What they can see is filtered below.
  const ownRoleKey = ownRoleEntry?.role || (isPrivileged ? 'org_admin' : null);
  const canViewAs = Boolean(user?.uid && activeOrg && canViewAsRole(ownRoleKey, isPrivileged));
  const viewerForVisibility = useMemo(
    () => (user?.uid ? { uid: user.uid, role: ownRoleKey, scopeId: ownRoleEntry?.scopeId || null, scopeType: ownRoleEntry?.scopeType || null, nwtId: ownRoleEntry?.nwtId || null, deptId: ownRoleEntry?.deptId || null, siteId: ownRoleEntry?.siteId || null, isSuperadmin: isPrivileged } : null),
    [user?.uid, ownRoleKey, ownRoleEntry, isPrivileged]
  );

  const storageKey = user?.uid && activeOrg?.id ? viewAsStorageKey(user.uid, activeOrg.id) : null;

  const [viewAsUid, setViewAsUidState] = useState(null);

  // Restore the per-user+org selection whenever the user or active org changes.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') {
      setViewAsUidState(null);
      return;
    }
    try {
      setViewAsUidState(window.localStorage.getItem(storageKey) || null);
    } catch {
      setViewAsUidState(null);
    }
  }, [storageKey]);

  const setViewAs = useCallback(
    (uidOrNull) => {
      setViewAsUidState(uidOrNull || null);
      if (storageKey && typeof window !== 'undefined') {
        try {
          if (uidOrNull) window.localStorage.setItem(storageKey, uidOrNull);
          else window.localStorage.removeItem(storageKey);
        } catch {
          /* storage unavailable — selection still applies this session */
        }
      }
    },
    [storageKey]
  );

  // Picking a generic persona lens always exits member mode.
  // Role-locked members can't switch lenses at all.
  // A scoped member may still pick a generic lens BELOW their own role (P4-04 downward only).
  const allowedLensRoles = useMemo(() => lensRolesFor(viewerForVisibility), [viewerForVisibility]);
  const setPersonaId = useCallback(
    (id) => {
      if (lockedPersonaId && !allowedLensRoles.includes(PERSONA_TO_ROLE_KEY[id])) return;
      setPersonaIdState(id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, id);
      }
      setViewAs(null);
    },
    [setViewAs, lockedPersonaId, allowedLensRoles]
  );

  // Live members of the active org (only subscribed when the switcher is usable).
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!activeOrg?.id || !canViewAs) {
      setMembers([]);
      return undefined;
    }
    const unsubscribe = subscribeToOrganizationMembers(activeOrg.id, setMembers);
    return () => unsubscribe();
  }, [activeOrg?.id, canViewAs]);

  // The member currently being viewed-as (null → self / generic persona mode).
  // Downward visibility (P4-04): the members this viewer may view as.
  const visibleMembers = useMemo(() => (canViewAs ? visibleMembersFor(viewerForVisibility, members, activeOrg) : []), [canViewAs, viewerForVisibility, members, activeOrg]);
  const lensRoles = useMemo(() => (canViewAs ? lensRolesFor(viewerForVisibility) : []), [canViewAs, viewerForVisibility]);

  const viewingAs = useMemo(() => {
    if (!canViewAs || !viewAsUid) return null;
    return visibleMembers.find((m) => m.uid === viewAsUid) || null;
  }, [canViewAs, viewAsUid, visibleMembers]);

  // P4-08: "View as yourself" returns to the viewer's OWN persona (their role), not to admin.
  const ownPersonaId = lockedPersonaId || ROLE_TO_PERSONA[ownRoleKey] || DEFAULT_PERSONA_ID;

  // Effective persona id: member mode maps the member's real role onto the
  // closest generic persona so DemoAccessGate / sidebar gating keep working.
  // Role-locked members always resolve to their own role's persona.
  const personaId = viewingAs
    ? ROLE_TO_PERSONA[viewingAs.role] || DEFAULT_PERSONA_ID
    : lockedPersonaId
      ? (allowedLensRoles.includes(PERSONA_TO_ROLE_KEY[basePersonaId]) ? basePersonaId : lockedPersonaId)
      : basePersonaId;

  const persona = useMemo(
    () => DEMO_PERSONAS.find((p) => p.id === personaId) || DEMO_PERSONAS[0],
    [personaId]
  );

  // personaUser: the viewed member when in member mode, else the REAL
  // signed-in user previewed in the selected generic role.
  const personaUser = useMemo(() => {
    if (viewingAs) {
      return {
        id: viewingAs.uid,
        name: viewingAs.displayName || viewingAs.email || 'Member',
        title: ROLE_DEFINITIONS[viewingAs.role]?.label || viewingAs.role,
      };
    }
    if (!user) return null;
    return {
      id: user.uid,
      name: user.displayName || user.email || 'You',
      title: persona.label,
    };
  }, [viewingAs, user, persona]);

  // Resolve org position → drives Firestore query scoping. Member mode uses
  // the member's REAL role entry; persona mode uses the first-unit heuristic.
  const queryScope = useMemo(() => {
    if (viewingAs) {
      return { personaId, ...resolveMemberScope(viewingAs, activeOrg) };
    }
    if (lockedPersonaId && ownRoleEntry) {
      // A real scoped member: their own role assignment IS their scope.
      return {
        personaId,
        ...resolveMemberScope(
          { uid: user?.uid, role: ownRoleEntry.role, scopeId: ownRoleEntry.scopeId },
          activeOrg
        ),
      };
    }
    return { personaId, ...resolveOrgScope(personaId, user?.uid || null, activeOrg) };
  }, [viewingAs, personaId, user?.uid, activeOrg, lockedPersonaId, ownRoleEntry]);

  // P4-06 / P4-07: tell the services who is at the keyboard and whether View As is read-only
  // for this viewer's role (org setting `viewAs.writeAccess`, default full access).
  const viewAsReadOnly = Boolean(viewingAs) && viewAsIsReadOnly(activeOrg, ownRoleKey);
  useEffect(() => {
    setActingContext({
      actingFor: viewingAs ? { uid: viewingAs.uid, name: viewingAs.displayName || viewingAs.email || '', role: viewingAs.role } : null,
      enteredBy: user ? { uid: user.uid, name: user.displayName || '', email: user.email || '', roleLabel: ROLE_DEFINITIONS[ownRoleKey]?.label || (isPrivileged ? 'Administrator' : '') } : null,
      readOnly: viewAsReadOnly,
      organizationId: activeOrg?.id || null,
    });
    return () => setActingContext(null);
  }, [viewingAs, user, ownRoleKey, isPrivileged, viewAsReadOnly, activeOrg?.id]);

  const value = useMemo(
    () => ({
      personaId,
      persona,
      personaUser,
      personas: DEMO_PERSONAS,
      setPersonaId,
      queryScope,
      // View-as (real-member impersonation)
      members,
      visibleMembers,
      lensRoles,
      ownPersonaId,
      ownRole: ownRoleKey,
      viewAsUid: viewingAs ? viewAsUid : null,
      setViewAs,
      viewingAs,
      canViewAs,
      // Helper booleans used by pages to gate content
      isAdmin: personaId === 'admin',
      isOrgIT: personaId === 'org_it',
      isExecutive: personaId === 'executive',
      isDH: personaId === 'dh',
      isFLS: personaId === 'fls',
      isITM: personaId === 'itm',
      isPPI: personaId === 'ppi',
    }),
    [personaId, persona, personaUser, queryScope, setPersonaId, members, visibleMembers, lensRoles, ownPersonaId, ownRoleKey, viewAsUid, setViewAs, viewingAs, viewAsReadOnly, canViewAs]
  );

  return <DemoRoleContext.Provider value={value}>{children}</DemoRoleContext.Provider>;
}

export function useDemoRole() {
  const ctx = useContext(DemoRoleContext);
  if (!ctx) {
    throw new Error('useDemoRole must be used within a DemoRoleProvider');
  }
  return ctx;
}

export default DemoRoleContext;
